import type { ISceneDB } from "../../../interfaces/Scenes";

import type { IStudio } from "../../../screens/Dashboard/SubComponents/Studio/interfaces/Studio.interface";

import type { StingerConfig } from "./SceneTransition";

import { openDatabase } from "../../../Utils/db";

import type LivePreview from "../LivePreview";

import PixiFactory from "./PixiFactory";

import PlayerFactory from "./PlayerFactory";

import AudioStrategy from "./StrategyAudio";

class StrategyDraw {
  private LivePreview: LivePreview;

  private Players!: PlayerFactory;

  private Audio!: AudioStrategy;

  private PixiFactory!: PixiFactory;

  constructor(instance: LivePreview) {
    this.LivePreview = instance;
  }

  async initialize() {
    this.Players = new PlayerFactory();

    await this.loadDraw();

    this.Audio = await this.loadAudio();
    performance.mark("eriol-start:audio");

    return this;
  }

  updateOnLive(onLive: boolean) {
    this.PixiFactory.updateOnLive(onLive);
  }

  async getScene(model: IStudio): Promise<ISceneDB> {
    const db = await openDatabase();

    const scenes = await db.scenes.toArray();

    const scene = scenes.find((scene: ISceneDB) => scene.model.id === model.id);

    return scene as ISceneDB;
  }

  async loadDraw() {
    const scene = await this.getScene(this.LivePreview.Model);
    // Marcas `eriol-start:*` del arranque: el OSR las suma a la línea de tiempo del broadcast
    // (BroadcastComposition) para atribuir un arranque lento a DB / canvas / GPU / audio.
    performance.mark("eriol-start:scene-db");

    // Pasa el AudioContext + destino del mix (GlobalAudio, ya seteado por

    // setAudioContext antes de play) para que el stinger pueda rutear su audio al

    // stream.

    this.PixiFactory = new PixiFactory(
      scene,

      this.Players,

      this.LivePreview.AudioContext,

      this.LivePreview.MediaStreamDestination,
    );

    await this.PixiFactory.createApp();
    performance.mark("eriol-start:pixi-app");

    this.PixiFactory.start();
  }

  async loadAudio() {
    const audio = new AudioStrategy(this.Players);

    await audio.connect();

    return audio;
  }

  async switchScene(scene: ISceneDB, isStreaming: boolean) {
    await this.PixiFactory.switchScene(scene, isStreaming);
  }

  async prewarmScene(scene: ISceneDB) {
    await this.PixiFactory.prewarmScene(scene);
  }

  async snapshotScene(scene: ISceneDB, width: number, height: number) {
    return this.PixiFactory.snapshotScene(scene, width, height);
  }

  async prewarmStinger(cfg: StingerConfig) {
    await this.PixiFactory.prewarmStinger(cfg);
  }

  stopScene() {
    this.PixiFactory.stop();
  }

  resumeScene() {
    this.PixiFactory.resumeScene();
  }

  async updateScene(scene: ISceneDB) {
    await this.PixiFactory.updateScene(scene);
  }

  async updateModel(model: IStudio) {
    await this.PixiFactory.updateModel(model);
  }

  stop() {
    this.PixiFactory.stop();
  }

  destroyPlayers() {
    this.PixiFactory.sceneDestroy();

    this.Players.destroy();
  }

  deleteArea(id: string) {
    this.Players.delete(id);

    this.PixiFactory.deleteArea(id);
  }

  destroy() {
    this.Audio.disconnect();

    this.PixiFactory.destroy();
  }

  getStream() {
    return this.PixiFactory.getStream();
  }
}

export default StrategyDraw;
